<template>
    <div class="public-question-screen">
        <div class="content">
            <div class="overlay" :class="{ 'open': isSidebarOpen }" @click.self="closeSidebar">
                <public-questions-sidebar ref="sidebar" :class="{ 'open': isSidebarOpen }"
                    :selectedQuestionId="selectedQuestionId" :initialQuestionId="initialQuestionId"
                    :initialQuestion="selectedQuestion" @select-question="selectQuestion" />
            </div>

            <div class="question-display">
                <HeaderMenuComponent title="Pregunta Pública" :permissions="permissions" :subscription="subscription"
                    :isMobile="isMobile" :showSubscribeButton="false" @toggle-sidebar="toggleSidebar" @logout="logout"
                    @show-toast="showToast" />

                <div v-if="loading" class="loading-container">
                    <div class="loading-spinner"></div>
                    <div class="loading-text">Cargando pregunta...</div>
                </div>

                <div v-else-if="!selectedQuestion" class="empty-state">
                    <div class="empty-message">Selecciona una pregunta para visualizar</div>
                </div>

                <div v-else class="question-content">
                    <div class="messages-container">
                        <div class="message-pair">
                            <HumanMessageComponent :text="selectedQuestion.q" :userName="'Pregunta'"
                                :timestamp="selectedQuestion.timestamp" />

                            <KotBotMessageComponent :text="selectedQuestion.a" :sources="selectedQuestion.sources || []"
                                :qanda_id="selectedQuestion._id" @show-toast="showToast"
                                :has_feedback="selectedQuestion.feedback !== null"
                                :timestamp="selectedQuestion.timestamp"
                                :conversation_id="selectedQuestion.conversation_id" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Toast notification -->
        <div v-if="toast.show" class="toast" :class="toast.type">
            {{ toast.message }}
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import HumanMessageComponent from '@/components/HumanMessageComponent.vue';
import KotBotMessageComponent from '@/components/KotBotMessageComponent.vue';
import HeaderMenuComponent from '@/components/HeaderMenuComponent.vue';
import PublicQuestionsSidebarComponent from '@/components/PublicQuestionsSidebarComponent.vue';
import { COUNTRY } from '../utils/countryConfig';

export default {
    name: "PublicQuestionScreen",
    components: {
        HumanMessageComponent,
        KotBotMessageComponent,
        HeaderMenuComponent,
        'public-questions-sidebar': PublicQuestionsSidebarComponent
    },
    data() {
        return {
            selectedQuestionId: null,
            initialQuestionId: null,
            selectedQuestion: null,
            loading: false,
            userName: '',
            userPicture: '',
            permissions: [],
            subscription: null,
            toast: {
                show: false,
                message: '',
                type: 'info',
                timeout: null
            },
            isMobile: false,
            isSidebarOpen: false
        };
    },
    props: {
        question_id: {
            type: String,
            required: true
        }
    },
    mounted() {
        // Get user info from localStorage
        this.userName = localStorage.getItem('user_name') || '';
        this.userPicture = localStorage.getItem('user_profile_picture') || '';

        // Parse permissions and subscription from localStorage
        try {
            const permissionsData = localStorage.getItem('user_permissions');
            if (permissionsData) {
                this.permissions = JSON.parse(permissionsData);
            }

            const subscriptionData = localStorage.getItem('user_subscription');
            if (subscriptionData) {
                this.subscription = JSON.parse(subscriptionData);
            }
        } catch (error) {
            console.error('Error parsing data from localStorage:', error);
        }

        // Set the initial question ID to prevent auto-selection in the sidebar
        this.initialQuestionId = this.question_id;

        // Load the question and set it as selected
        this.selectQuestion(this.question_id);

        // Check if device is mobile
        this.checkMobile();

        // Add resize event listener
        window.addEventListener('resize', this.checkMobile);
    },
    beforeUnmount() {
        // Remove resize event listener
        window.removeEventListener('resize', this.checkMobile);
    },
    methods: {
        async selectQuestion(questionId) {
            if (this.selectedQuestionId === questionId) return;

            this.selectedQuestionId = questionId;
            this.loading = true;

            // Update URL with the selected question ID
            this.$router.replace({
                query: { ...this.$route.query, id: questionId }
            });

            try {
                const response = await axios.get(`${process.env.VUE_APP_BASE_URL}/api/v2/public-questions/get/`, {
                    params: {
                        qanda_id: questionId,
                        country: COUNTRY
                    }
                });

                this.selectedQuestion = response.data;

                // If this is the initial question ID, make sure it's set
                if (questionId === this.question_id && !this.initialQuestionId) {
                    this.initialQuestionId = questionId;
                }
            } catch (error) {
                console.error('Error loading question details:', error);
                this.selectedQuestion = null;
                this.showToast('Error al cargar la pregunta. Intenta nuevamente más tarde.', 'error');
            } finally {
                this.loading = false;
            }
        },

        goToPublicQuestions() {
            this.$router.push({ name: "public-questions" });
        },

        showToast(message, type = 'info') {
            // Clear any existing timeout
            if (this.toast.timeout) {
                clearTimeout(this.toast.timeout);
            }

            // Show the toast
            this.toast.show = true;
            this.toast.message = message;
            this.toast.type = type;

            // Set a timeout to hide the toast
            this.toast.timeout = setTimeout(() => {
                this.toast.show = false;
            }, 3000);
        },

        checkMobile() {
            this.isMobile = window.innerWidth <= 768;
        },

        closeSidebar() {
            this.isSidebarOpen = false;
        },

        toggleSidebar() {
            // Force the sidebar to be visible
            this.isSidebarOpen = !this.isSidebarOpen;

            // Log the state for debugging
            console.log('PublicQuestionScreen - isSidebarOpen:', this.isSidebarOpen);

            // Force the sidebar to be visible by directly manipulating the DOM if needed
            if (this.isSidebarOpen) {
                const sidebar = this.$refs.sidebar?.$el;
                if (sidebar) {
                    sidebar.style.transform = 'translateX(0) !important';
                    sidebar.classList.add('open');
                }
            }
        },

        logout() {
            // Clear user data from localStorage
            localStorage.removeItem('user_email');
            localStorage.removeItem('user_name');
            localStorage.removeItem('user_permissions');
            localStorage.removeItem('user_profile_picture');
            localStorage.removeItem('user_subscription');

            this.showToast('Sesión cerrada exitosamente', 'success');

            // Redirect to login page after a short delay
            setTimeout(() => {
                this.$router.push('/login');
            }, 1000);
        }
    }
};
</script>

<style scoped>
.public-question-screen {
    display: flex;
    flex-direction: column;
    height: 100vh;
    background-color: #f5f5f5;
    color: #333333;
    width: 100%;
    position: relative;
}

.content {
    display: flex;
    flex: 1;
    overflow: hidden;
    position: relative;
}

.question-display {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: #f5f5f5;
}

.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid #a1344e;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.loading-text {
    font-size: 16px;
    color: #666666;
}

.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}

.empty-message {
    font-size: 16px;
    color: #666666;
    text-align: center;
    max-width: 300px;
}

.question-content {
    flex: 1;
    overflow-y: auto;
    padding: 24px;
}

.messages-container {
    max-width: 1200px;
    margin: 0 auto;
}

.message-pair {
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
}

/* Toast notification */
.toast {
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    padding: 12px 20px;
    border-radius: 4px;
    color: white;
    font-weight: 500;
    z-index: 1000;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    min-width: 250px;
    text-align: center;
}

.toast.info {
    background-color: #2196F3;
}

.toast.success {
    background-color: #4CAF50;
}

.toast.warning {
    background-color: #FF9800;
}

.toast.error {
    background-color: #F44336;
}

@media screen and (max-width: 768px) {
    .question-content {
        padding: 15px;
    }

    .messages-container {
        padding: 0;
    }

    .overlay.open {
        position: fixed;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        width: 100vw;
        height: 100vh;
        z-index: 10;
        background: rgba(0, 0, 0, 0.75);
    }
}
</style>
